// Integration catalog: every named integration with a docs deep-link.
// Each entry maps to a Monofor help-center article so users land in real
// instructions instead of a marketing dead-end.

export type Protocol =
  | 'saml'
  | 'oidc'
  | 'oauth'
  | 'radius'
  | 'browser'
  | 'native'

export interface Integration {
  name: string
  category: string
  protocols: Protocol[]
  /** help.monofor.com deep link; absent while the per-app article is being written. */
  href?: string
  /** Short vendor description (from the product catalog). */
  description?: string
  highlight?: boolean // surface in the "featured" row
}

export const protocolLabels: Record<Protocol, string> = {
  saml: 'SAML 2.0',
  oidc: 'OIDC',
  oauth: 'OAuth 2',
  radius: 'RADIUS',
  browser: 'Browser ext.',
  native: 'Native',
}

export const integrations: Integration[] = [
  // --- Cloud platforms ---
  {
    name: 'AWS',
    category: 'Cloud platforms',
    protocols: ['saml'],
    href: 'https://help.monofor.com/monosign/aws-saml-integration',
    highlight: true,
  },
  {
    name: 'Microsoft 365',
    category: 'Cloud platforms',
    protocols: ['saml'],
    href: 'https://help.monofor.com/monosign/office-365-integration',
    highlight: true,
  },
  {
    name: 'Google Workspace',
    category: 'Cloud platforms',
    protocols: ['saml'],
    href: 'https://help.monofor.com/monosign/google-workspace-saml-integration',
    highlight: true,
  },
  {
    name: 'GitHub',
    category: 'Cloud platforms',
    protocols: ['saml'],
    href: 'https://help.monofor.com/monosign/github-saml-integration',
  },
  {
    name: 'Cloudflare',
    category: 'Cloud platforms',
    protocols: ['saml', 'oidc'],
    href: 'https://help.monofor.com/monosign/cloudflare-saml-integration',
  },
  {
    name: 'VMware vCenter',
    category: 'Cloud platforms',
    protocols: ['oidc'],
    href: 'https://help.monofor.com/monosign/vmware-vcenter-vsphere-client-oidc-integration',
  },

  // --- VPN & remote access ---
  {
    name: 'Fortigate SSL VPN',
    category: 'VPN & remote access',
    protocols: ['saml', 'radius'],
    href: 'https://help.monofor.com/monosign/fortigate-ssl-vpn-saml-integration',
    highlight: true,
  },
  {
    name: 'Palo Alto SSL VPN',
    category: 'VPN & remote access',
    protocols: ['saml', 'radius'],
    href: 'https://help.monofor.com/monosign/palo-alto-ssl-vpn-saml-integration',
  },
  {
    name: 'Pulse Secure VPN',
    category: 'VPN & remote access',
    protocols: ['radius'],
    href: 'https://help.monofor.com/monosign/pulse-secure-vpn-radius-integration',
  },
  {
    name: 'Ivanti Connect Secure',
    category: 'VPN & remote access',
    protocols: ['saml'],
    href: 'https://help.monofor.com/monosign/ivanti-connect-secure-saml2-integration',
  },
  {
    name: 'OpenVPN',
    category: 'VPN & remote access',
    protocols: ['saml'],
    href: 'https://help.monofor.com/monosign/openvpn-saml-integration',
  },
  {
    name: 'Fortigate Admin Login',
    category: 'VPN & remote access',
    protocols: ['saml'],
    href: 'https://help.monofor.com/monosign/fortigate-admin-login-saml-integration',
  },
  {
    name: 'BeyondTrust Remote Support',
    category: 'VPN & remote access',
    protocols: ['radius'],
    href: 'https://help.monofor.com/monosign/beyondtrust-remote-support-radius-integration',
  },

  // --- Microsoft stack ---
  {
    name: 'Microsoft OWA (Outlook Web)',
    category: 'Microsoft stack',
    protocols: ['saml'],
    href: 'https://help.monofor.com/monosign/microsoft-owa-outlook-web-access-adfs-saml',
    highlight: true,
  },
  {
    name: 'Exchange Admin Center (ECP)',
    category: 'Microsoft stack',
    protocols: ['saml'],
    href: 'https://help.monofor.com/monosign/microsoft-ecp-eac-exchange-admin-center-adfs-saml',
  },
  {
    name: 'Exchange OWA · MFA',
    category: 'Microsoft stack',
    protocols: ['native'],
    href: 'https://help.monofor.com/monosign/microsoft-exchange-owa-mfa',
  },
  {
    name: 'SharePoint 2019 (on-prem)',
    category: 'Microsoft stack',
    protocols: ['saml'],
    href: 'https://help.monofor.com/monosign/sharepoint-2019-on-premise-saml-ws-federation-inte',
  },
  {
    name: 'Microsoft IIS',
    category: 'Microsoft stack',
    protocols: ['native'],
    href: 'https://help.monofor.com/monosign/microsoft-iis-integration',
  },

  // --- Developer & DevOps ---
  {
    name: 'Jenkins',
    category: 'Developer & DevOps',
    protocols: ['saml'],
    href: 'https://help.monofor.com/monosign/jenkins-saml-integration',
  },
  {
    name: 'ArgoCD',
    category: 'Developer & DevOps',
    protocols: ['saml'],
    href: 'https://help.monofor.com/monosign/argocd-saml-integration',
  },
  {
    name: 'Grafana',
    category: 'Developer & DevOps',
    protocols: ['oidc'],
    href: 'https://help.monofor.com/monosign/grafana-oidc-integration',
  },
  {
    name: 'Harbor',
    category: 'Developer & DevOps',
    protocols: ['oidc'],
    href: 'https://help.monofor.com/monosign/harbor-oidc-integration',
  },
  {
    name: 'Portainer',
    category: 'Developer & DevOps',
    protocols: ['oauth'],
    href: 'https://help.monofor.com/monosign/portainer-oauth2-integration',
  },
  {
    name: 'SonarQube',
    category: 'Developer & DevOps',
    protocols: ['saml'],
    href: 'https://help.monofor.com/monosign/sonarqube-saml-integration',
  },
  {
    name: 'Checkmarx',
    category: 'Developer & DevOps',
    protocols: ['saml'],
    href: 'https://help.monofor.com/monosign/checkmarx-saml-integration',
  },
  {
    name: 'Dependency-Track',
    category: 'Developer & DevOps',
    protocols: ['oidc'],
    href: 'https://help.monofor.com/monosign/dependency-track-openid-intg',
  },

  // --- Collaboration & support ---
  {
    name: 'Zendesk',
    category: 'Collaboration & support',
    protocols: ['saml', 'oidc'],
    href: 'https://help.monofor.com/monosign/zendesk-saml-integration',
  },
  {
    name: 'Jira',
    category: 'Collaboration & support',
    protocols: ['saml'],
    href: 'https://help.monofor.com/monosign/jira-saml-integration',
    highlight: true,
  },
  {
    name: 'Intercom',
    category: 'Collaboration & support',
    protocols: ['saml'],
    href: 'https://help.monofor.com/monosign/intercom-saml-integration',
  },
  {
    name: 'PagerDuty',
    category: 'Collaboration & support',
    protocols: ['saml'],
    href: 'https://help.monofor.com/monosign/pagerduty-saml-integration',
  },
  {
    name: 'Grammarly',
    category: 'Collaboration & support',
    protocols: ['saml'],
    href: 'https://help.monofor.com/monosign/grammarly-saml-integration',
  },
  {
    name: 'Evernote',
    category: 'Collaboration & support',
    protocols: ['saml'],
    href: 'https://help.monofor.com/monosign/evernote-saml-integration',
  },
  {
    name: 'Wiki.js',
    category: 'Collaboration & support',
    protocols: ['saml'],
    href: 'https://help.monofor.com/monosign/wiki-js-saml-integration',
  },
  {
    name: 'Drupal',
    category: 'Collaboration & support',
    protocols: ['saml', 'oidc'],
    href: 'https://help.monofor.com/monosign/drupal-saml-integration',
  },
  {
    name: 'Nextcloud',
    category: 'Collaboration & support',
    protocols: ['saml'],
    href: 'https://help.monofor.com/monosign/nextcloud-saml-integration',
  },
  {
    name: 'Odoo',
    category: 'Collaboration & support',
    protocols: ['oidc'],
    href: 'https://help.monofor.com/monosign/odoo-oidc-integration',
  },
  {
    name: 'Zimbra',
    category: 'Collaboration & support',
    protocols: ['saml'],
    href: 'https://help.monofor.com/monosign/zimbra-saml-integration',
  },
  {
    name: 'Pipedrive',
    category: 'Collaboration & support',
    protocols: ['saml'],
    href: 'https://help.monofor.com/monosign/pipedrive-saml-integration',
  },

  // --- Infrastructure & monitoring ---
  {
    name: 'Splunk (on-prem)',
    category: 'Infrastructure & monitoring',
    protocols: ['saml'],
    href: 'https://help.monofor.com/monosign/splunk-on-prem-saml-integration',
  },
  {
    name: 'Zabbix',
    category: 'Infrastructure & monitoring',
    protocols: ['saml'],
    href: 'https://help.monofor.com/monosign/zabbix-saml-integration',
  },
  {
    name: 'Veeam',
    category: 'Infrastructure & monitoring',
    protocols: ['saml'],
    href: 'https://help.monofor.com/monosign/veeam-saml-integration',
  },
  {
    name: 'Desktop Central',
    category: 'Infrastructure & monitoring',
    protocols: ['saml'],
    href: 'https://help.monofor.com/monosign/desktop-central-saml-integration',
  },
  {
    name: 'Vectra',
    category: 'Infrastructure & monitoring',
    protocols: ['saml'],
    href: 'https://help.monofor.com/monosign/vectra-saml-integration',
  },
  {
    name: 'Trellix',
    category: 'Infrastructure & monitoring',
    protocols: ['saml'],
    href: 'https://help.monofor.com/monosign/trellix-saml-integration',
  },
  {
    name: 'Attivo BotSink',
    category: 'Infrastructure & monitoring',
    protocols: ['saml'],
    href: 'https://help.monofor.com/monosign/attivo-botsink-saml-integration',
  },
  {
    name: 'Keepnet Labs',
    category: 'Infrastructure & monitoring',
    protocols: ['saml'],
    href: 'https://help.monofor.com/monosign/keepnet-labs-saml-integration',
  },

  // --- Operating systems & RADIUS ---
  {
    name: 'Windows passwordless login',
    category: 'OS & desktop',
    protocols: ['native'],
    href: 'https://help.monofor.com/monosign/windows-passwordless-login',
    highlight: true,
  },
  {
    name: 'macOS passwordless login',
    category: 'OS & desktop',
    protocols: ['native'],
    href: 'https://help.monofor.com/monosign/macos-passwordless-login',
  },
  {
    name: 'Ubuntu / Debian (RADIUS)',
    category: 'OS & desktop',
    protocols: ['radius'],
    href: 'https://help.monofor.com/monosign/ubuntu-debian-radius-authentication',
  },
  {
    name: 'Kerberos authentication',
    category: 'OS & desktop',
    protocols: ['native'],
    href: 'https://help.monofor.com/monosign/kerberos-authentication',
  },

  // --- Social & web ---
  {
    name: 'Facebook (browser ext.)',
    category: 'Social & web',
    protocols: ['browser'],
    href: 'https://help.monofor.com/monosign/facebook-browser-extension-integration',
  },
  {
    name: 'Instagram (browser ext.)',
    category: 'Social & web',
    protocols: ['browser'],
    href: 'https://help.monofor.com/monosign/instagram-browser-extension-integration',
  },
  {
    name: 'Twitter (browser ext.)',
    category: 'Social & web',
    protocols: ['browser'],
    href: 'https://help.monofor.com/monosign/twitter-browser-extension-integration',
  },

  // --- Custom & generic ---
  {
    name: 'Custom REST application',
    category: 'Custom & generic',
    protocols: ['native'],
    href: 'https://help.monofor.com/monosign/custom-application-rest-api-integration',
    highlight: true,
  },
  {
    name: 'Custom OpenID Connect app',
    category: 'Custom & generic',
    protocols: ['oidc'],
    href: 'https://help.monofor.com/monosign/custom-openid-oidc-application',
  },
  {
    name: 'Generic browser actions',
    category: 'Custom & generic',
    protocols: ['browser'],
    href: 'https://help.monofor.com/monosign/generic-browser-actions',
  },

  // --- Wave 2: popular apps from the product catalog (no per-app doc yet) ---
  // Collaboration
  {
    name: 'Slack',
    category: 'Collaboration',
    protocols: ['browser', 'saml'],
    description: 'Slack is the single workspace that connects you with the people and tools you work with everyday, no matter where you are or what you do.',
  },
  {
    name: 'Zoom',
    category: 'Collaboration',
    protocols: ['browser'],
    description: 'Zoom brings people and teams together to get more done in a frictionless and secure video environment.',
  },
  {
    name: 'Miro',
    category: 'Collaboration',
    protocols: ['browser', 'saml'],
    description: 'Miro is an online, visual collaboration platform designed to unlock creativity and accelerate innovation among teams of all kinds.',
  },
  {
    name: 'Notion',
    category: 'Collaboration',
    protocols: ['browser', 'saml'],
    description: 'Notion is the all-in-one workspace for notes, project management, documents, and collaboration.',
  },
  {
    name: 'Airtable',
    category: 'Collaboration',
    protocols: ['saml'],
    description: 'Airtable is an app platform that enables teams to build workflows that modernize their business processes.',
  },
  {
    name: 'Trello',
    category: 'Collaboration',
    protocols: ['saml'],
    description: 'Spreadsheets, emails, and constant notifications are all major blockers affecting today\'s business teams.',
  },
  {
    name: 'Asana',
    category: 'Collaboration',
    protocols: ['browser', 'saml'],
    description: 'Asana is the task manager to get your teams working.',
  },
  {
    name: 'ClickUp',
    category: 'Collaboration',
    protocols: ['saml'],
    description: 'ClickUp is a productivity platform that makes your personal and work lives more productive, saving you time.',
  },
  {
    name: 'Smartsheet',
    category: 'Collaboration',
    protocols: ['saml', 'browser'],
    description: 'Smartsheet enables teams to manage projects, automate processes & scale programs in one powerful platform.',
  },
  {
    name: 'Basecamp',
    category: 'Collaboration',
    protocols: ['browser'],
    description: 'Trusted by millions, Basecamp puts everything that is needed to get work done in one place.',
  },
  {
    name: 'Dropbox Business',
    category: 'Collaboration',
    protocols: ['browser', 'saml'],
    description: 'Dropbox is a cloud-based file storage and collaboration platform for the modern work environment.',
  },
  {
    name: 'Box',
    category: 'Collaboration',
    protocols: ['browser', 'saml'],
    description: 'Box is a modern content management platform for companies of all sizes and industries.',
  },
  {
    name: 'Lucidchart',
    category: 'Collaboration',
    protocols: ['browser', 'saml'],
    description: 'Lucidchart is your solution for visual communication and cross-platform collaboration.',
  },
  {
    name: 'Mural',
    category: 'Collaboration',
    protocols: ['saml'],
    description: 'Mural is a collaborative intelligence company.',
  },
  // Sales & marketing
  {
    name: 'Salesforce',
    category: 'Sales & marketing',
    protocols: ['saml', 'browser'],
    description: 'Salesforce is an online solution for customer relationship management, or CRM.',
  },
  {
    name: 'HubSpot',
    category: 'Sales & marketing',
    protocols: ['saml'],
    description: 'HubSpot is a complete CRM platform with all the tools you need to grow better, whether you want to increase leads, accelerate sales, streamline customer service, or build a…',
  },
  {
    name: 'Mailchimp',
    category: 'Sales & marketing',
    protocols: ['browser'],
    description: 'Mailchimp\'s all-in-one Marketing Platform helps businesses deliver the right message at the right moment.',
  },
  {
    name: 'Marketo',
    category: 'Sales & marketing',
    protocols: ['browser', 'saml'],
    description: 'Marketo Engage helps marketing teams drive demand and growth by delivering exceptional experiences that align with sales teams at every stage of the customer journey.',
  },
  {
    name: 'Zoho CRM',
    category: 'Sales & marketing',
    protocols: ['saml', 'browser'],
    description: 'Zoho provides a cloud-based customer relationship management (CRM) platform that caters to businesses and industries of all sizes and types.',
  },
  {
    name: 'Hootsuite',
    category: 'Sales & marketing',
    protocols: ['browser', 'saml'],
    description: 'We created Hootsuite to help people connect on social media and do amazing things together.',
  },
  {
    name: 'SurveyMonkey',
    category: 'Sales & marketing',
    protocols: ['browser', 'saml'],
    description: 'Use SurveyMonkey to drive your business forward by using our free online survey tool to capture the voices and opinions of the people who matter most to you.',
  },
  {
    name: 'Typeform',
    category: 'Sales & marketing',
    protocols: ['browser'],
    description: 'Create beautiful, next generation online forms with Typeform.',
  },
  {
    name: 'Calendly',
    category: 'Sales & marketing',
    protocols: ['browser', 'saml'],
    description: 'Calendly is scheduling software that makes meetings happen by eliminating friction in finding times to meet.',
  },
  {
    name: 'DocuSign',
    category: 'Sales & marketing',
    protocols: ['browser', 'saml'],
    description: 'DocuSign is the global leader in electronic signatures and the only solution provider of a comprehensive agreement cloud.',
  },
  // DevOps & engineering
  {
    name: 'GitLab',
    category: 'DevOps & engineering',
    protocols: ['browser'],
    description: 'GitLab is a complete DevOps platform, delivered as a single application.',
  },
  {
    name: 'Bitbucket',
    category: 'DevOps & engineering',
    protocols: ['browser'],
    description: 'Collaborate on code with inline comments and pull requests.',
  },
  {
    name: 'Atlassian Jira Cloud',
    category: 'DevOps & engineering',
    protocols: ['browser'],
    description: 'Plan, track, and manage your agile and software development projects in Jira.',
  },
  {
    name: 'Atlassian Confluence Cloud',
    category: 'DevOps & engineering',
    protocols: ['browser'],
    description: 'Confluence is where you create, organize and discuss work with your team.',
  },
  {
    name: 'Datadog',
    category: 'DevOps & engineering',
    protocols: ['browser'],
    description: 'Datadog is a monitoring and security platform for cloud applications.',
  },
  {
    name: 'New Relic',
    category: 'DevOps & engineering',
    protocols: ['browser', 'saml'],
    description: 'With New Relic One you get instant observability with our Quickstarts that make it easy to instrument in a few clicks.',
  },
  {
    name: 'Sentry',
    category: 'DevOps & engineering',
    protocols: ['browser', 'saml'],
    description: 'Sentry is a code observability platform that helps developers monitor application health from the frontend to the backend.',
  },
  {
    name: 'Amazon Managed Grafana',
    category: 'DevOps & engineering',
    protocols: ['saml'],
    description: 'Amazon Managed Grafana is a highly scalable, highly available, and fully managed Grafana service, providing interactive data visualization across multiple data sources.',
  },
  {
    name: 'Docker Hub',
    category: 'DevOps & engineering',
    protocols: ['browser'],
    description: 'Docker helps developers bring their ideas to life by conquering the complexity of app development.',
  },
  {
    name: 'Postman',
    category: 'DevOps & engineering',
    protocols: ['saml'],
    description: 'Postman is a collaboration platform for API development.',
  },
  {
    name: 'CircleCI',
    category: 'DevOps & engineering',
    protocols: ['browser'],
    description: 'CircleCI provides development teams the confidence to build, test, and deploy.',
  },
  {
    name: 'Terraform Cloud',
    category: 'DevOps & engineering',
    protocols: ['saml'],
    description: 'Terraform Cloud is an application that helps teams use Terraform together.',
  },
  {
    name: 'Splunk Cloud',
    category: 'DevOps & engineering',
    protocols: ['browser'],
    description: 'Use Monofor’s EventBridge integration to monitor System Log events for suspicious activity, automate actions to mitigate risks, receive real time alerts about issues,…',
  },
  {
    name: 'Sumo Logic',
    category: 'DevOps & engineering',
    protocols: ['browser', 'saml'],
    description: 'Sumo Logic is the pioneer of continuous intelligence (CI) software.',
  },
  // Support & ITSM
  {
    name: 'Zendesk Sell (Formerly Base CRM)',
    category: 'Sales & marketing',
    protocols: ['saml', 'browser'],
    description: 'Formerly Base CRM. Standard integration for Single Sign-On will allow your users seemless authentication through all applications.',
  },
  {
    name: 'Freshservice',
    category: 'Support & ITSM',
    protocols: ['saml'],
    description: 'Standard integration for Single Sign-On will allow your users seemless authentication through all applications.',
  },
  {
    name: 'ServiceNow',
    category: 'Support & ITSM',
    protocols: ['browser', 'saml'],
    description: 'ServiceNow delivers the IT, Employee, and Customer Workflows that matter with enterprise solutions to help drive every part of your digital transformation.',
  },
  {
    name: 'Atlassian Jira Service Desk',
    category: 'Support & ITSM',
    protocols: ['browser'],
    description: 'IT service desk and customer service software by Jira Service Desk.',
  },
  {
    name: 'OpsGenie',
    category: 'Support & ITSM',
    protocols: ['saml'],
    description: 'Opsgenie is a modern incident management platform for operating always-on services, empowering DevOps teams to stay in control during incidents.',
  },
  {
    name: 'StatusPage',
    category: 'Support & ITSM',
    protocols: ['saml', 'browser'],
    description: 'StatusPage is now Atlassian Product, please use Atlassian Atlas.',
  },
  {
    name: 'LiveChat',
    category: 'Support & ITSM',
    protocols: ['browser', 'saml'],
    description: 'Live chat software for business.',
  },
  // HR & finance
  {
    name: 'Workday',
    category: 'HR & finance',
    protocols: ['browser'],
    description: 'The Workday Customer Center is an online case management system accessed by Named Support Contacts to report customer issues.',
  },
  {
    name: 'BambooHR',
    category: 'HR & finance',
    protocols: ['browser', 'saml'],
    description: 'BambooHR is the No.',
  },
  {
    name: 'Greenhouse',
    category: 'HR & finance',
    protocols: ['saml'],
    description: 'Greenhouse delivers a complete solution to help companies hire for what\'s next.',
  },
  {
    name: 'Lever',
    category: 'HR & finance',
    protocols: ['saml'],
    description: 'Streamline your hiring process with an applicant tracking system that bridges the gap between recruiters and the rest of your team.',
  },
  {
    name: 'Gusto',
    category: 'HR & finance',
    protocols: ['browser'],
    description: 'Gustos people platform helps businesses onboard, pay, insure, and support hardworking team.',
  },
  {
    name: 'Expensify',
    category: 'HR & finance',
    protocols: ['browser', 'saml'],
    description: 'Expensify is the world\'s leading application for expense management, receipt scanning, and business travel.',
  },
  {
    name: 'NetSuite',
    category: 'HR & finance',
    protocols: ['browser', 'saml'],
    description: 'NetSuite provides cloud financials, CRM, ecommerce, HCM and professional services automation management for all organizations from fast-growing midsize companies to large…',
  },
  {
    name: 'QuickBooks',
    category: 'HR & finance',
    protocols: ['browser'],
    description: 'QuickBooks is a full-featured business and financial management suite complete with tools for accounting, inventory, payroll, tax filing, invoicing, bank account tracking and…',
  },
  {
    name: 'Xero',
    category: 'HR & finance',
    protocols: ['browser'],
    description: 'Xero connects you to all things business.',
  },
  {
    name: 'SAP Concur',
    category: 'HR & finance',
    protocols: ['browser', 'saml'],
    description: 'SAP Concur simplifies travel,  expense  and invoice management for total visibility and greater control.',
  },
  {
    name: 'ADP OneHR',
    category: 'HR & finance',
    protocols: ['browser'],
    description: 'OneHR provides integrated payroll, social benefits, HRMS and dispatching services to help employers in China.',
  },
  {
    name: 'Namely',
    category: 'HR & finance',
    protocols: ['saml'],
    description: 'Namely covers your essential HR needs and compliance requirements in one place.',
  },
  {
    name: '15five',
    category: 'HR & finance',
    protocols: ['browser', 'saml'],
    description: '15Five\'s mission is to create highly-engaged, high performing organizations by helping people become their best selves.',
  },
  {
    name: 'Culture Amp',
    category: 'HR & finance',
    protocols: ['browser'],
    description: 'Culture Amp Employee surveys that improve workplace culture and performance.',
  },
  // Analytics & data
  {
    name: 'Tableau Cloud',
    category: 'Analytics & data',
    protocols: ['saml'],
    description: 'Tableau Cloud is your analytics platform fully hosted in the cloud, that allows your organization to build, share, and collaborate around data discoveries.',
  },
  {
    name: 'Looker',
    category: 'Analytics & data',
    protocols: ['browser', 'saml'],
    description: 'Looker is a business intelligence software and big data analytics platform that helps explore, analyze and share real-time business analytics easily.',
  },
  {
    name: 'Amplitude',
    category: 'Analytics & data',
    protocols: ['browser'],
    description: 'Amplitude helps users understand user behavior, ship improved experiences, and retain more customers.',
  },
  {
    name: 'Mixpanel',
    category: 'Analytics & data',
    protocols: ['browser', 'saml'],
    description: 'Secure access to Mixpanel leveraging your Monofor setup.',
  },
  {
    name: 'Segment',
    category: 'Analytics & data',
    protocols: ['browser', 'saml'],
    description: 'Segment is a customer data platform that helps your teams access clean and reliable data to make real-time decisions, accelerate growth, and personalize experiences.',
  },
  {
    name: 'Snowflake',
    category: 'Analytics & data',
    protocols: ['browser', 'saml'],
    description: 'Snowflake is the only data warehouse built for the cloud for all your data and all your users.',
  },
  {
    name: 'Databricks',
    category: 'Analytics & data',
    protocols: ['saml'],
    description: 'Databricks Unified Analytics Platform, from the original creators of Apache Spark, unifies data science and engineering across the Machine Learning lifecycle from data…',
  },
  {
    name: 'Heap',
    category: 'Analytics & data',
    protocols: ['browser', 'saml'],
    description: 'Monofor\'s integration with Heap allows you to authenticate Heap users using Monofor.',
  },
  {
    name: 'Hotjar',
    category: 'Analytics & data',
    protocols: ['browser'],
    description: 'Hotjar is a way to truly understand your web and mobile site visitors.',
  },
  // Design & product
  {
    name: 'Figma',
    category: 'Design & product',
    protocols: ['saml'],
    description: 'Design software, prototype, and gather feedback all in one place with Figma.',
  },
  {
    name: 'InVision',
    category: 'Design & product',
    protocols: ['saml'],
    description: 'InVision is the Digital Product Design and Development platform used to make the world’s best customer experiences.',
  },
  {
    name: 'Sketch',
    category: 'Design & product',
    protocols: ['saml'],
    description: 'Sketch is the all-in-one platform for digital design: everything you need for collaborative design, prototyping and developer handoff.',
  },
  {
    name: 'Canva',
    category: 'Design & product',
    protocols: ['browser', 'saml'],
    description: 'Canva is your photo editor, video editor, and graphic design tool all in one app.',
  },
  {
    name: 'Adobe',
    category: 'Design & product',
    protocols: ['browser'],
    description: 'Adobe Acrobat online services let you work with PDFs in any browser.',
  },
  {
    name: 'Zeplin',
    category: 'Design & product',
    protocols: ['browser'],
    description: 'Connected space for product teams.',
  },
  {
    name: 'Productboard',
    category: 'Design & product',
    protocols: ['saml'],
    description: 'This app integration supports Single Sign-On and Provisioning.',
  },
  // Security & IT
  {
    name: '1Password Business',
    category: 'Security & IT',
    protocols: ['browser'],
    description: 'A password manager, digital vault, form filler and secure digital wallet.',
  },
  {
    name: 'LastPass',
    category: 'Security & IT',
    protocols: ['browser'],
    description: 'Protect your business from cyber-threats, without compromising ease of use.',
  },
  {
    name: 'Dashlane',
    category: 'Security & IT',
    protocols: ['saml'],
    description: 'The Dashlane integration with Monofor will facilitate our joint clients by allowing a seamless SSO setup with Dashlane.',
  },
  {
    name: 'KnowBe4',
    category: 'Security & IT',
    protocols: ['saml'],
    description: 'KnowBe4 is the world’s largest integrated security awareness training and simulated phishing platform.',
  },
  {
    name: 'Tenable.io',
    category: 'Security & IT',
    protocols: ['browser'],
    description: 'Tenable.',
  },
  {
    name: 'Qualys Guard',
    category: 'Security & IT',
    protocols: ['browser', 'saml'],
    description: 'Provider of on demand IT security risk and compliance management solutions.',
  },
  {
    name: 'Rapid7',
    category: 'Security & IT',
    protocols: ['browser'],
    description: 'Rapid7 powers the practice of SecOps by delivering shared visibility, analytics, and automation to unite security, IT, and DevOps teams.',
  },
]

export const categories: string[] = Array.from(
  new Set(integrations.map((i) => i.category))
)

// ---------------------------------------------------------------------------
// Programmatic integration pages (/integrations/[slug])
// ---------------------------------------------------------------------------

export function integrationSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export function getIntegrationBySlug(slug: string): Integration | undefined {
  return integrations.find((i) => integrationSlug(i.name) === slug)
}

export function relatedIntegrations(current: Integration, limit = 6): Integration[] {
  const sameCategory = integrations.filter(
    (i) => i.category === current.category && i.name !== current.name
  )
  const others = integrations.filter(
    (i) => i.category !== current.category && i.name !== current.name
  )
  return [...sameCategory, ...others].slice(0, limit)
}
